import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core'
import { FormGroup, FormControl, Validators,  FormGroupDirective } from '@angular/forms'
import { takeUntil } from 'rxjs/operators'
import { UnsubscribeOnDestroy } from '../../../../../utility'
import { AbstractControl } from '@angular/forms'

@Component({
    selector: 'app-form-input-password',
    templateUrl: 'form-input-password.html'
})
export class FormInputPasswordComponent extends UnsubscribeOnDestroy implements OnInit, OnChanges {

    @Input() label: string = 'Password'
    @Input() value: string | undefined = ''
    @Input() placeholderText: string = 'Enter your password'
    @Input() fieldId: string = 'password'
    @Input() darkTheme: boolean = false
    @Input() type: 'set' | 'get' = 'get'
    @Input() modifierClassname: string
    @Input() useMinWidth = false
    @Input() required = true

    form: FormGroup
    passwordVisible = false
    validations: {[key: string]: boolean} = {
        upperCaseMatch: false,
        lowerCaseMatch: false,
        digitMatch: false,
        specialCharacterMatch: false,
        lengthMatch: false
    }
    matchCount = 0
    passwordStrength: 'weak' | 'medium' | 'strong' | 'very strong' | ''

    constructor(private rootFormGroup: FormGroupDirective) {
        super()
    }

    ngOnInit(): void {
        this.form = this.rootFormGroup.control;

        const validators = this.getValidators()

        this.placeholderText = this.type === 'set' ? 'Create your password' : this.placeholderText

        this.form.addControl(
            this.fieldId, 
            new FormControl(this.value, validators)
        )

        this.form
            .valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.passwordStrength = this.checkPasswordStrength()
            })
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.required?.isFirstChange()) { return }
        if (changes?.required?.previousValue !== changes?.required?.currentValue) {
            if (changes.required.currentValue) {
                this.addValidators()
            } else {
                this.removeValidators()    
            }
        }
    }

    getValidators() {
        return [
            this.required ? Validators.required : null,
            this.type === 'set' && this.required ? this.isValid.bind(this) : null
        ].filter(Boolean)
    }

    removeValidators() {
        this.form.get(this.fieldId).clearValidators()
        this.form.get(this.fieldId).updateValueAndValidity()  
    }
         
    addValidators() {
        this.form.get(this.fieldId).setValidators(this.getValidators())
        this.form.get(this.fieldId).updateValueAndValidity() 
    }

    checkPasswordStrength() {
        return this.matchCount > 4 ? 'very strong' : 
            this.matchCount > 3 ? 'strong' :
            this.matchCount > 2 ? 'medium' :
            this.matchCount > 1 ? 'weak' : ''
    }

    checkMatchCount() {
        let count = 0
        Object.values(this.validations).map(value => {
            if (value) count ++
        })

        return count
    }   

    isValid(control: AbstractControl) {
        // Must be at lease 8 characters long and have 3 of the 4 conditions below met.
        this.validations = {
            upperCaseMatch: this.checkUpperCaseMatch(control.value),
            lowerCaseMatch: this.checkLowerCaseMatch(control.value),
            digitMatch: this.checkDigitMatch(control.value),
            specialCharacterMatch: this.checkSpecialCharacterMatch(control.value),
            lengthMatch: this.checkLengthMatch(control.value)
        }

        this.matchCount = this.checkMatchCount()

        return this.validations.lengthMatch && this.matchCount > 3 ? null : { isValid : false }
    }

    checkUpperCaseMatch(value: string) {
        return !!value.match(/^(?=.*[A-Z])/)  
    }

    checkLowerCaseMatch(value: string) {
        return !!value.match(/(?=.*[a-z])/) 
    }

    checkDigitMatch(value: string) {
        return !!value.match(/(.*[0-9].*)/)
    }

    checkSpecialCharacterMatch(value: string) {
        return !!value.match(/(?=.*[!@#$%^&*])/)
    }

    checkLengthMatch(value: string) {
        return !!value.match(/.{8,}/)
    }

    get control(){
        return this.form.get(this.fieldId)
    }

    showPassword() {
        this.passwordVisible = !this.passwordVisible
    }
}
